// Podcast Ad's form (live's `podcast-ad-form/library-view`, spec G14-templates PR 16, captures
// 114 to 130): live's staged form. The script stage picks the product, the host and the guest,
// writes the script (the server's director, or the member) and approves it; the cast stage shows
// the five stills the server draws from the approved script. "Generate video" is PR B.
import { Group, Loader, Stack, Text, Tooltip } from "@mantine/core";
import { Smartphone, Timer } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { ActorSource, PodcastScript } from "../../../api/generated/model";
import {
  VideoTemplateQuote,
  VideoTemplateRequest,
  podcastAdInputsCtaMax,
  podcastAdInputsPronunciationMax,
  podcastAdInputsTopicMax,
  type PodcastAdInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { ActorsComposer } from "../../../platform/actors/composer/ActorsComposer";
import { actorModels } from "../../../platform/actors/composer/GenerateWithText";
import { pickedOfActor, type PickedActor } from "../../../platform/actors/picked";
import { useMakings } from "../../../platform/actors/use-makings";
import { Choice } from "../../../ui/Choice";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { Submit, Subtitles } from "./staged";
import { CastStage } from "./PodcastCast";
import { STILL_ORDER, usePodcastDraft, videoBody, type DraftWork } from "./podcast-draft";
import {
  ACTOR_PENDING,
  GENERATE_SCRIPT,
  LENGTHS,
  MAX_LINE_WORDS,
  PICK_PRODUCT,
  STILLS_PENDING,
  UNQUOTED_CLAIMS,
  blankScript,
  sameLineTexts,
  scriptIssue,
  scriptMeta,
} from "./podcast-script";
import { InputChip, ScriptSection } from "./PodcastScript";
import {
  CastTile,
  ProductTile,
  ROLES,
  sourceKey,
  useProductPick,
  type CastRole,
  type PickedPictures,
  type RoleMaking,
} from "./PodcastTiles";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const LENGTH_TOOLTIP = "Ad length: 45, 60 or 80 seconds — 11, 15 or 17 lines.";
const FORMAT_TOOLTIP = "Every image and clip is rendered vertical 9:16 at 1080p.";
const APPROVE_SCRIPT = "Approve script";
const SCRIPT_TOOLTIP =
  "One line per clip, written from your product page. Edit any line before approving — every " +
  "product fact is quoted from the page.";
const WRITE_SCRIPT = "Write the lines yourself";
const SCRIPT_HINT = {
  wand: "to write the conversation from the product page, or",
  pencil: "to write the lines yourself.",
};
const GENERATE_VIDEO = "Generate video";
const HANDOFF_NOTICE = "Your podcast ad is finishing in My Generations — you can close this panel now.";
const CHIPS = [
  {
    field: "topic",
    label: "Topic",
    placeholder: "The buying mistake… (optional)",
    tooltip:
      "The buying mistake the conversation corrects. Leave empty and the director picks it from " +
      "your product page.",
    maxLength: podcastAdInputsTopicMax,
  },
  {
    field: "cta",
    label: "CTA",
    placeholder: "Shop now (optional)",
    tooltip: "The button label on the end card. Leave empty and the director writes a short one.",
    maxLength: podcastAdInputsCtaMax,
  },
  {
    field: "pronunciation",
    label: "Pronunciation",
    placeholder: "vuh-MOOSE (optional)",
    tooltip:
      "How your brand name is said, e.g. vuh-MOOSE. Leave empty and the director decides what " +
      "needs a respelling.",
    maxLength: podcastAdInputsPronunciationMax,
  },
] as const;

/** Live's progress line under the form (`toDraftProgressLabel`). */
function progressOf(work: DraftWork, drawn: number): string | null {
  if (work.writing) return "Writing the script";
  if (work.planning) return "Planning the shots";
  return work.drawing ? `Generating images · ${drawn}/${STILL_ORDER.length}` : null;
}

/**
 * Live's form: "Reuse example" (off and disabled: the card has no example), the stage, and in the
 * footer the length, the subtitles and the 9:16 format, and the round start, "Approve script"
 * then "Generate video", whose tooltip says what it waits for. The draft is the server's
 * (`podcast-draft.ts`), so it is as the member left it when the panel opens again.
 */
export function PodcastAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = usePodcastDraft(teamId, brandId);
  const catalog = useGetCatalog().data;
  // Live's "Add AI Actor": the actor composer in the form's place, and the actor it makes.
  const [composing, setComposing] = useState<CastRole | null>(null);
  const [makings, setMakings] = useState<RoleMaking[]>([]);
  const [pictures, setPictures] = useState<PickedPictures>({});
  // Live's `scriptBaseline`: the script as last written for the member (the draft's as the form
  // opened it, the director's answer once kept, or Write's blank lines). The lines' issue shows
  // once they differ from it, or once approval was asked (live's `isSubmitted`).
  const [baseline, setBaseline] = useState<PodcastScript | null | undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [wasWriting, setWasWriting] = useState(false);
  // Generate Script without a product: live's "Pick a product and its packshot" at its tile.
  const [productAsked, setProductAsked] = useState(false);
  const { form, draft } = flow;
  const product = useProductPick(teamId, brandId, form?.inputs.product ?? null);
  // Live's "Generate video": the run finishes in My Generations; closing the panel stops nothing.
  const video = draft === undefined || form === null ? null : videoBody(draft, form.inputs);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted,
  });
  if (draft !== undefined && baseline === undefined) setBaseline(draft.script);
  if (flow.work.writing !== wasWriting) {
    setWasWriting(flow.work.writing);
    // The director's answer, kept, is the script as written (live's `setScript`); a failed one
    // leaves the baseline as it was.
    if (!flow.work.writing && draft !== undefined && draft.script_director !== "failed") {
      setBaseline(draft.script);
    }
  }

  const setInputs = (change: Partial<PodcastAdInputsOutput>) => flow.editInputs(change);
  const cast = (role: CastRole, source: ActorSource | null) =>
    setInputs(role === "host" ? { host: source } : { guest: source });
  const pick = (role: CastRole, actor: PickedActor) => {
    setPictures((current) => ({
      ...current,
      [sourceKey(actor.source)]: { url: actor.imageUrl, name: actor.name },
    }));
    cast(role, actor.source);
  };
  useMakings({
    teamId,
    brandId,
    makings,
    onMade: (jobId, made) => {
      const making = makings.find((each) => each.jobId === jobId);
      setMakings((current) => current.filter((each) => each.jobId !== jobId));
      if (making !== undefined) pick(making.role, pickedOfActor(made));
    },
    onFailed: (jobId) =>
      setMakings((current) => current.map((each) => (each.jobId === jobId ? { ...each, failed: true } : each))),
  });

  const panel = (body: ReactNode, footer?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      // Podcast Ad's card has no example, so live's switch stays off and disabled.
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Podcast Ad</Text>}
      footer={footer}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null) return panel(<Loader size="sm" aria-label="Loading the draft" />);

  const makers = catalog === undefined ? [] : actorModels(catalog.image_models);
  if (composing !== null && catalog !== undefined) {
    const role = composing;
    const close = () => setComposing(null);
    return (
      <ActorsComposer
        teamId={teamId}
        brandId={brandId}
        catalog={catalog}
        onStarted={(job) => {
          setMakings((current) => [
            ...current.filter((each) => each.role !== role),
            { jobId: job.id, failed: false, role },
          ]);
          close();
        }}
        place={{
          outputs: 1,
          uploadMost: 1,
          onCancel: close,
          onCreated: (made) => {
            const actor = made[0];
            if (actor !== undefined) pick(role, pickedOfActor(actor));
            close();
          },
        }}
      />
    );
  }

  const { inputs, script } = form;
  const casting = draft.stage === "cast";
  // Live's locked form: nothing changes while a director or a still works, or once approved.
  const locked = !flow.editable;
  const work = flow.work;
  const drawn = draft.stills.filter((each) => each.status === "done").length;
  const progress = progressOf(work, drawn);
  const issue = scriptIssue(script, inputs.length);
  const edited = script !== null && !sameLineTexts(script, baseline ?? null);
  const shownIssue = script !== null && !work.writing && (edited || submitted) ? issue : null;
  const makingFor = (role: CastRole) => makings.find((each) => each.role === role) ?? null;

  let submit;
  if (casting) {
    const ready = drawn === STILL_ORDER.length;
    // Live's "Generate video" (PR B): the run from the approved script, spec and stills.
    const starting = videoStart.start.isPending;
    submit = (
      <Submit
        label={GENERATE_VIDEO}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || starting}
        loading={starting}
        onClick={videoStart.startJob}
      />
    );
  } else {
    // Live's tooltip names only these; a script's own issue keeps the button off (the form's
    // validity) and shows under the script alone.
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(script === null ? [GENERATE_SCRIPT] : []),
      ...(makings.length > 0 ? [ACTOR_PENDING] : []),
    ];
    submit = (
      <Submit
        label={APPROVE_SCRIPT}
        reasons={reasons}
        disabled={reasons.length > 0 || issue !== null || flow.busy}
        loading={flow.approving}
        onClick={() => {
          setSubmitted(true);
          flow.approve();
        }}
      />
    );
  }

  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Choice
          setting="Length"
          icon={<Timer size={16} strokeWidth={1.5} />}
          tip={LENGTH_TOOLTIP}
          width={82}
          chevron={false}
          value={inputs.length}
          options={LENGTHS.map((each) => ({ value: each, label: `${each} s` }))}
          disabledBecause={locked ? LENGTH_TOOLTIP : undefined}
          onChange={(next) => {
            const length = LENGTHS.find((each) => each === next);
            if (length !== undefined) setInputs({ length });
          }}
        />
        {/* Live keeps Subtitles open at the cast too; ours cannot keep a change there (the draft
            takes none once approved), so PR B's Generate video sends the captions it starts with. */}
        <Subtitles
          on={inputs.subtitles}
          position={inputs.caption_position}
          disabled={locked}
          onChange={(change) => setInputs(change)}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const failure = refused === null ? null : <ErrorAlert error={refused} />;
  const status = (
    <>
      {draft.problem === null ? null : (
        <Text className={classes.problem} role="alert">
          {draft.problem}
        </Text>
      )}
      {failure}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {/* Live's toast, said in the panel (web/AGENTS.md: no toasts), and its hand-off line. */}
      {videoStart.start.isSuccess && casting ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
    </>
  );
  const body = casting ? (
    <div className={classes.content}>
      <CastStage
        stills={draft.stills}
        spec={draft.spec}
        planning={work.planning}
        busy={flow.busy}
        resetting={flow.resetting}
        onRegenerate={flow.regenerate}
        onFixLabel={flow.fixLabel}
        onStartOver={flow.startOver}
      />
      {status}
    </div>
  ) : (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <ProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            disabled={locked}
            missing={productAsked}
            onChange={(next) => setInputs({ product: next })}
          />
          {(["host", "guest"] as const).map((role) => (
            <CastTile
              key={role}
              teamId={teamId}
              brandId={brandId}
              label={ROLES[role].label}
              tooltip={ROLES[role].tooltip}
              source={inputs[role] ?? null}
              making={makingFor(role)}
              pictures={pictures}
              disabled={locked}
              onMakeActor={makers.length === 0 ? null : () => setComposing(role)}
              onPick={(actor) => pick(role, actor)}
              onRemove={() => {
                setMakings((current) => current.filter((each) => each.role !== role));
                cast(role, null);
              }}
            />
          ))}
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<PodcastAdInputsOutput> = {};
                change[chip.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        <ScriptSection
          lines={script?.lines ?? null}
          meta={script === null ? null : scriptMeta(script.lines, inputs.length)}
          tooltip={SCRIPT_TOOLTIP}
          primary="HOST"
          maxWords={() => MAX_LINE_WORDS}
          hint={SCRIPT_HINT}
          writeLabel={WRITE_SCRIPT}
          writing={work.writing}
          locked={locked}
          issue={shownIssue}
          warning={draft.script_warning && script !== null ? UNQUOTED_CLAIMS : null}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onWrite={() => {
            const blank = blankScript({
              topic: inputs.topic.trim(),
              length: inputs.length,
              productName: product.name,
              cta: inputs.cta.trim(),
            });
            setBaseline(blank);
            flow.edit({ script: blank });
          }}
          onLine={(index, text) => {
            if (script === null) return;
            flow.edit({
              script: {
                ...script,
                lines: script.lines.map((line, at) => (at === index ? { ...line, text } : line)),
              },
            });
          }}
        />
      </Stack>
      {status}
    </div>
  );
  return panel(body, footer);
}
